/**
 * Client Management Page - Client table with search, filter, and CRUD actions
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import {
  Plus,
  Search,
  Pencil,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  ArrowRightLeft,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { ReadOnlyModuleBanner } from '../../components/ui/ReadOnlyModuleBanner';
import { ResponsiveTableWrapper } from '../../components/ui';
import { useClientList, useAllAssignableProviders } from '../../hooks/useClients';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useModuleGuard } from '../../hooks/useModuleGuard';
import { useIsSuperAdmin } from '../../hooks/useIsSuperAdmin';
import { useAuthStore } from '../../stores/authStore';
import type { Client } from '../../types/clients';
import { getLocations } from '../../services/api/locationService';
import { CLIENT_STATUS_BADGE_VARIANT, CLIENT_STATUS_LABEL } from './clientFormConstants';
import { CreateClientModal } from './CreateClientModal';
import { EditClientModal } from './EditClientModal';
import { BulkClientUploadModal } from './BulkClientUploadModal';
import { CaseloadTransferModal } from './CaseloadTransferModal';
import { AssignProvidersPanel } from '../../components/clients/AssignProvidersPanel';
import { SetOverseeingBcbaModal } from '../../components/clients/SetOverseeingBcbaModal';
import { ClientDetailDrawer } from '../../components/clients/ClientDetailDrawer';
import {
  BulkAssignProvidersPanel,
  type BulkMode,
} from '../../components/clients/BulkAssignProvidersPanel';
import { BulkBcbaPanel, type BcbaMode } from '../../components/clients/BulkBcbaPanel';
import { BulkStatusModal } from '../../components/clients/BulkStatusModal';

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    width: 100%;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 280px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const FilterContainer = styled.div`
  min-width: 140px;
`;

const TableWrapper = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
`;

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.backgrounds.gray};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  white-space: nowrap;
`;

const ThActions = styled(Th)`
  width: 60px;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  cursor: pointer;

  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  vertical-align: middle;
`;

const ClientNameLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary.ocean};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: inherit;
  padding: 0;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const SecondaryText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const BcbaCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.primary.base};
`;

const ThCheck = styled(Th)`
  width: 40px;
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.colors.primary.lightSky ?? theme.colors.backgrounds.light};
  border: 1px solid ${({ theme }) => theme.colors.primary.base};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const SelectionCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-right: auto;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const SkeletonBar = styled.div<{ $width?: string }>`
  height: 14px;
  width: ${({ $width }) => $width ?? '100%'};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
  }
`;

const PaginationInfo = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PaginationControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  align-items: center;
`;

const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// ==================== Helpers ====================

// The blank option does NOT mean "every record" — inactive clients are off the
// roster and are only reachable by explicitly selecting "Inactive" (Org Admins
// only, enforced server-side). Labelled to say exactly that.
const FILTER_STATUS_OPTIONS = [
  { value: '', label: 'All Roster Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DISCHARGED', label: 'Discharged' },
  { value: 'ON_HOLD', label: 'On Hold' },
];

const INACTIVE_FILTER_OPTION = { value: 'INACTIVE', label: 'Inactive (off roster)' };

const formatDOB = (dob?: string) => {
  if (!dob) return '—';
  return new Date(dob).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// ==================== Component ====================

export const ClientManagementPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [centerFilter, setCenterFilter] = useState('');
  const [clinicianFilter, setClinicianFilter] = useState('');
  const [bcbaFilter, setBcbaFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [clientToEdit, setClientToEdit] = useState<Client | null>(null);
  const [clientToAssign, setClientToAssign] = useState<Client | null>(null);
  const [clientForBcba, setClientForBcba] = useState<Client | null>(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  // Bulk selection — persists across pagination + search/center-filter changes.
  const [selectedClients, setSelectedClients] = useState<
    Map<string, { id: string; firstName: string; lastName: string }>
  >(new Map());
  const [bulkMode, setBulkMode] = useState<BulkMode | null>(null);
  const [bcbaMode, setBcbaMode] = useState<BcbaMode | null>(null);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);

  const navigate = useNavigate();
  const { id: selectedClientId } = useParams<{ id?: string }>();
  const { hasPermission } = useAuthStore();
  const isSuperAdmin = useIsSuperAdmin();

  // Stable reference — prevents the drawer's Escape-key effect from re-subscribing on every render
  const handleDrawerClose = useCallback(() => navigate('/clients'), [navigate]);
  const { isReadonly: clientsReadonly } = useModuleGuard('clients');

  // Debounce the search text so the list query fires once the user pauses typing.
  const debouncedSearch = useDebouncedValue(search, 300);

  // Reset to the first page whenever the (debounced) search changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading } = useClientList({
    page,
    limit: 20,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    centerId: centerFilter || undefined,
    clinicianMemberId: clinicianFilter || undefined,
    overseeingBcbaMemberId: bcbaFilter || undefined,
  });

  const { data: locationsData } = useQuery({
    queryKey: ['locations', 'all-for-clients'],
    queryFn: () => getLocations({ limit: 200 }),
  });

  const centerOptions = useMemo(
    () => [
      { value: '', label: 'All Centers' },
      ...(locationsData?.data ?? []).map((loc) => ({ value: loc.id, label: loc.name })),
    ],
    [locationsData],
  );

  const { data: providersData } = useAllAssignableProviders();

  const clinicianOptions = useMemo(
    () => [
      { value: '', label: 'All Clinicians' },
      ...(providersData ?? []).map((p) => ({
        value: p.memberId,
        label: `${p.firstName} ${p.lastName}`.trim(),
      })),
    ],
    [providersData],
  );

  const bcbaOptions = useMemo(
    () => [
      { value: '', label: 'All BCBAs' },
      ...(providersData ?? []).map((p) => ({
        value: p.memberId,
        label: `${p.firstName} ${p.lastName}`.trim(),
      })),
    ],
    [providersData],
  );

  const clients = data?.data ?? [];
  const meta = data?.meta;
  const totalPages = meta?.totalPages ?? 1;
  const from = meta ? (meta.page - 1) * meta.limit + 1 : 0;
  const to = meta ? Math.min(meta.page * meta.limit, meta.total) : 0;

  // ── Bulk selection helpers ──────────────────────────────────────────────
  const selectedIds = useMemo(() => [...selectedClients.keys()], [selectedClients]);
  const allPageSelected = clients.length > 0 && clients.every((c) => selectedClients.has(c.id));
  const somePageSelected = clients.some((c) => selectedClients.has(c.id));

  const toggleClient = (client: Client) =>
    setSelectedClients((prev) => {
      const next = new Map(prev);
      if (next.has(client.id)) next.delete(client.id);
      else
        next.set(client.id, {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
        });
      return next;
    });

  const toggleSelectPage = () =>
    setSelectedClients((prev) => {
      const next = new Map(prev);
      if (allPageSelected) {
        clients.forEach((c) => next.delete(c.id));
      } else {
        clients.forEach((c) =>
          next.set(c.id, { id: c.id, firstName: c.firstName, lastName: c.lastName }),
        );
      }
      return next;
    });

  const clearSelection = () => setSelectedClients(new Map());

  // Selection already persists across pagination/search/center-filter changes by
  // design, but the status filter crosses the roster/Inactive boundary — a stale
  // roster selection carried into the Inactive view (or vice versa) would let
  // "Update Status" silently act on now-hidden records. Clear it on that switch.
  useEffect(() => {
    clearSelection();
  }, [statusFilter]);

  // Inactive clients are off the roster and visible only to Org Admins
  // (`clients:manage:all`) / super admins — the same gate the API enforces, so the
  // option is not offered to anyone whose request would be rejected.
  const canSeeInactive = hasPermission('clients:manage:all') || isSuperAdmin;
  const statusOptions = useMemo(
    () =>
      canSeeInactive ? [...FILTER_STATUS_OPTIONS, INACTIVE_FILTER_OPTION] : FILTER_STATUS_OPTIONS,
    [canSeeInactive],
  );
  // All client actions — add (single/bulk), edit, set/reset BCBA, transfer caseload, and
  // assign/unassign providers — are gated by a dedicated permission (org admin only) on top
  // of the READ-only module lock. Users without it get a read-only client list.
  const canManage = hasPermission('clients:manage:actions') && !clientsReadonly;
  // The inactive selection is read-only per record: the API rejects every write
  // against an off-roster client, so row-level affordances (select, edit, assign)
  // are hidden while it is showing. Global actions (Add Client, Bulk Upload,
  // Transfer Caseload) don't target the shown rows and stay available.
  const viewingInactive = statusFilter === 'INACTIVE';
  const canManageRows = canManage && !viewingInactive;
  // Table column count for colSpan/skeleton math: 5 fixed columns (Name, DOB, Status,
  // Center, Assigned BCBA) + the checkbox column (canManage) + the row-actions column
  // (canManageRows).
  const columnCount = 5 + (canManage ? 1 : 0) + (canManageRows ? 1 : 0);

  const handleEditClick = (e: React.MouseEvent, client: Client) => {
    e.stopPropagation();
    setClientToEdit(client);
  };

  return (
    <Container>
      {clientsReadonly && (
        <ReadOnlyModuleBanner
          message="Client management is in read-only mode — your current plan or license does not permit adding or editing clients. Contact your administrator to upgrade."
          data-testid="clients-readonly-banner"
        />
      )}
      <Header>
        <Title>Clients</Title>
        <HeaderActions>
          <SearchContainer>
            <Input
              type="search"
              placeholder="Search clients..."
              leftIcon={<Search />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
            />
          </SearchContainer>
          <FilterContainer>
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              fullWidth
            />
          </FilterContainer>
          <FilterContainer>
            <Select
              options={centerOptions}
              value={centerFilter}
              onChange={(e) => {
                setCenterFilter(e.target.value);
                setPage(1);
              }}
              fullWidth
            />
          </FilterContainer>
          <FilterContainer>
            <Select
              options={clinicianOptions}
              value={clinicianFilter}
              onChange={(e) => {
                setClinicianFilter(e.target.value);
                setPage(1);
              }}
              fullWidth
            />
          </FilterContainer>
          <FilterContainer>
            <Select
              options={bcbaOptions}
              value={bcbaFilter}
              onChange={(e) => {
                setBcbaFilter(e.target.value);
                setPage(1);
              }}
              fullWidth
            />
          </FilterContainer>
          {canManage && (
            <Button
              variant="secondary"
              leftIcon={<ArrowRightLeft />}
              onClick={() => setShowTransferModal(true)}
            >
              Transfer Caseload
            </Button>
          )}
          {canManage && (
            <>
              <Button
                variant="secondary"
                leftIcon={<Plus />}
                onClick={() => setShowBulkUpload(true)}
              >
                Bulk Upload
              </Button>
              <Button
                variant="primary-blue"
                leftIcon={<Plus />}
                onClick={() => setShowCreateModal(true)}
              >
                Add Client
              </Button>
            </>
          )}
        </HeaderActions>
      </Header>

      {canManage && selectedClients.size > 0 && (
        <SelectionBar>
          <SelectionCount>{selectedClients.size} selected</SelectionCount>
          {!viewingInactive && (
            <>
              <Button variant="primary" size="sm" onClick={() => setBulkMode('assign')}>
                Assign providers
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setBulkMode('unassign')}>
                Unassign providers
              </Button>
              <Button variant="primary" size="sm" onClick={() => setBcbaMode('assign')}>
                Assign BCBA
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setBcbaMode('unassign')}>
                Unassign BCBA
              </Button>
            </>
          )}
          <Button variant="secondary" size="sm" onClick={() => setShowBulkStatusModal(true)}>
            Update Status
          </Button>
          <Button variant="ghost" size="sm" onClick={clearSelection}>
            Clear
          </Button>
        </SelectionBar>
      )}

      <TableWrapper>
        <ResponsiveTableWrapper>
          <Table>
            <Thead>
              <tr>
                {canManage && (
                  <ThCheck>
                    <Checkbox
                      type="checkbox"
                      checked={allPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allPageSelected && somePageSelected;
                      }}
                      onChange={toggleSelectPage}
                      aria-label="Select all clients on this page"
                    />
                  </ThCheck>
                )}
                <Th>Name</Th>
                <Th>Date of Birth</Th>
                <Th>Status</Th>
                <Th>Center</Th>
                <Th>Assigned BCBA</Th>
                {canManageRows && <ThActions />}
              </tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {canManage && <Td />}
                    <Td>
                      <SkeletonBar $width="160px" />
                    </Td>
                    <Td>
                      <SkeletonBar $width="100px" />
                    </Td>
                    <Td>
                      <SkeletonBar $width="70px" />
                    </Td>
                    <Td>
                      <SkeletonBar $width="120px" />
                    </Td>
                    <Td>
                      <SkeletonBar $width="130px" />
                    </Td>
                    {canManageRows && <Td />}
                  </tr>
                ))
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={columnCount}>
                    <EmptyState>
                      {search || statusFilter || centerFilter
                        ? 'No clients match your filters.'
                        : 'No clients yet. Add your first client.'}
                    </EmptyState>
                  </td>
                </tr>
              ) : (
                clients.map((client) => (
                  <Tr key={client.id} onClick={() => navigate(`/clients/${client.id}`)}>
                    {canManage && (
                      <Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          type="checkbox"
                          checked={selectedClients.has(client.id)}
                          onChange={() => toggleClient(client)}
                          aria-label={`Select ${client.firstName} ${client.lastName}`}
                        />
                      </Td>
                    )}
                    <Td>
                      <ClientNameLink
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/clients/${client.id}`);
                        }}
                      >
                        {client.firstName} {client.lastName}
                      </ClientNameLink>
                    </Td>
                    <Td>
                      <SecondaryText>{formatDOB(client.dateOfBirth)}</SecondaryText>
                    </Td>
                    <Td>
                      <Badge variant={CLIENT_STATUS_BADGE_VARIANT[client.status]}>
                        {CLIENT_STATUS_LABEL[client.status]}
                      </Badge>
                    </Td>
                    <Td>
                      <SecondaryText>{client.primaryCenter?.name ?? '—'}</SecondaryText>
                    </Td>
                    <Td>
                      <BcbaCell>
                        <SecondaryText>
                          {client.supervisingClinician
                            ? `${client.supervisingClinician.firstName} ${client.supervisingClinician.lastName}`
                            : '—'}
                        </SecondaryText>
                        {canManageRows && (
                          <Button
                            variant="outlined"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setClientForBcba(client);
                            }}
                          >
                            {client.supervisingClinicianId ? 'Replace' : 'Set'}
                          </Button>
                        )}
                      </BcbaCell>
                    </Td>
                    {canManageRows && (
                      <Td>
                        <RowActions>
                          <IconButton
                            title="Assign providers"
                            aria-label="Assign providers"
                            onClick={(e) => {
                              e.stopPropagation();
                              setClientToAssign(client);
                            }}
                          >
                            <UserPlus />
                          </IconButton>
                          <IconButton
                            title="Edit client"
                            aria-label="Edit client"
                            onClick={(e) => handleEditClick(e, client)}
                          >
                            <Pencil />
                          </IconButton>
                        </RowActions>
                      </Td>
                    )}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </ResponsiveTableWrapper>

        {!isLoading && clients.length > 0 && meta && (
          <Pagination>
            <PaginationInfo>
              Showing {from}–{to} of {meta.total} client{meta.total !== 1 ? 's' : ''}
            </PaginationInfo>
            <PaginationControls>
              <PageButton
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft />
              </PageButton>
              <PageButton
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                aria-label="Next page"
              >
                <ChevronRight />
              </PageButton>
            </PaginationControls>
          </Pagination>
        )}
      </TableWrapper>

      <CreateClientModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />

      <EditClientModal
        isOpen={!!clientToEdit}
        onClose={() => setClientToEdit(null)}
        client={clientToEdit}
      />

      <BulkClientUploadModal isOpen={showBulkUpload} onClose={() => setShowBulkUpload(false)} />

      <CaseloadTransferModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
      />

      {clientToAssign && (
        <AssignProvidersPanel
          clientId={clientToAssign.id}
          clientName={`${clientToAssign.firstName} ${clientToAssign.lastName}`}
          onClose={() => setClientToAssign(null)}
        />
      )}

      {clientForBcba && (
        <SetOverseeingBcbaModal
          isOpen
          clientId={clientForBcba.id}
          clientName={`${clientForBcba.firstName} ${clientForBcba.lastName}`}
          currentSupervisingClinicianId={clientForBcba.supervisingClinicianId}
          currentSupervisingClinicianName={
            clientForBcba.supervisingClinician
              ? `${clientForBcba.supervisingClinician.firstName} ${clientForBcba.supervisingClinician.lastName}`
              : null
          }
          onClose={() => setClientForBcba(null)}
        />
      )}

      {bulkMode && (
        <BulkAssignProvidersPanel
          clientIds={selectedIds}
          mode={bulkMode}
          onClose={() => setBulkMode(null)}
          onDone={() => {
            setBulkMode(null);
            clearSelection();
          }}
        />
      )}

      {bcbaMode && (
        <BulkBcbaPanel
          clientIds={selectedIds}
          mode={bcbaMode}
          onClose={() => setBcbaMode(null)}
          onDone={() => {
            setBcbaMode(null);
            clearSelection();
          }}
        />
      )}

      {showBulkStatusModal && (
        <BulkStatusModal
          clientIds={selectedIds}
          onClose={() => setShowBulkStatusModal(false)}
          onDone={() => {
            setShowBulkStatusModal(false);
            clearSelection();
          }}
        />
      )}

      {/* The drawer derives read-only state from the loaded client itself
          (off-roster ⇒ read-only), so it gets the base permission gate. */}
      {selectedClientId && (
        <ClientDetailDrawer
          clientId={selectedClientId}
          onClose={handleDrawerClose}
          canManage={canManage}
        />
      )}
    </Container>
  );
};

export default ClientManagementPage;
